// Internal buffer representation used by every transform. Operating on
// plain Float32Array channels (rather than the browser's AudioBuffer)
// lets every transform be pure-math and fully unit-testable in Node.
//
// Convert at the boundary:
//   toPcm(audioBuffer)  — right before the pipeline
//   fromPcm(ctx, pcm)   — right before playback / render

export type PcmBuffer = {
  sampleRate: number;
  channels: Float32Array[];
};

export function toPcm(buffer: AudioBuffer): PcmBuffer {
  const channels: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    // Copy rather than alias — downstream transforms mutate freely.
    const src = buffer.getChannelData(c);
    const out = new Float32Array(src.length);
    out.set(src);
    channels.push(out);
  }
  return { sampleRate: buffer.sampleRate, channels };
}

export function fromPcm(
  ctx: { createBuffer(channels: number, length: number, rate: number): AudioBuffer },
  pcm: PcmBuffer,
): AudioBuffer {
  const nCh = Math.max(1, pcm.channels.length);
  const length = pcm.channels[0]?.length ?? 0;
  const buf = ctx.createBuffer(nCh, length, pcm.sampleRate);
  for (let c = 0; c < nCh; c++) {
    // copyToChannel expects Float32Array<ArrayBuffer>; copy through a
    // fresh array to satisfy the stricter TS lib type even when the
    // source came from a shared buffer.
    const src = pcm.channels[c]!;
    const copy = new Float32Array(src.length);
    copy.set(src);
    buf.copyToChannel(copy, c);
  }
  return buf;
}

export function durationSeconds(pcm: PcmBuffer): number {
  const length = pcm.channels[0]?.length ?? 0;
  return length / pcm.sampleRate;
}

/** Build a PcmBuffer from per-channel Float32 arrays. Enforces uniform
 *  length — returns a wider array when channels differ (shorter padded
 *  with zeros), because mixing uneven channels is always a bug upstream. */
export function pcmFromChannels(
  sampleRate: number,
  channels: Float32Array[],
): PcmBuffer {
  const len = channels.reduce((m, c) => Math.max(m, c.length), 0);
  const normalized = channels.map((c) => {
    if (c.length === len) return c;
    const out = new Float32Array(len);
    out.set(c);
    return out;
  });
  return { sampleRate, channels: normalized };
}
